import type { CSSProperties, FC, HTMLAttributes, KeyboardEventHandler, ReactNode } from 'react';
import { Button, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { TbArrowLeft } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { SearchFilterInput } from '@/shared/components/inputs';
import OverflowFadeScrollArea from '@/shared/components/layouts/OverflowFadeScrollArea';

import DropdownOptionContent from '../DropdownOptionContent';
import type { DropdownOptionAvatarProps } from '../DropdownOptionContent';

interface DropdownPanelSearchProps {
    ariaLabel?: string;
    autoFocus?: boolean;
    bordered?: boolean;
    disabled?: boolean;
    iconColor?: CSSProperties['color'];
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    placeholder: string;
    setValue: (value: string) => void;
    size?: 'small' | 'middle' | 'large';
    suffix?: ReactNode;
    value: string;
}

interface DropdownPanelProps {
    actions?: ReactNode;
    children: ReactNode;
    contentInnerStyle?: CSSProperties;
    contentProps?: Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'style'>;
    contentScrollDisabled?: boolean;
    contentStyle?: CSSProperties;
    footer?: ReactNode;
    header?: ReactNode;
    maxHeight?: CSSProperties['maxHeight'];
    minWidth?: CSSProperties['minWidth'];
    search?: DropdownPanelSearchProps;
    style?: CSSProperties;
    width?: CSSProperties['width'];
}

/**
 * Lets the shared panel own scrolling without clipping its elevation in the
 * Ant Dropdown wrapper. Wrapper clipping presents as a rectangular gray fill
 * behind wide, right-aligned dropdown triggers.
 */
const FLOATING_DROPDOWN_OVERLAY_STYLE: CSSProperties = {
    maxHeight: 'none',
    overflow: 'visible',
};

const DropdownPanel: FC<DropdownPanelProps> = ({
    actions,
    children,
    contentInnerStyle,
    contentProps,
    contentScrollDisabled = false,
    contentStyle,
    footer,
    header,
    maxHeight = 'min(30rem, 70vh)',
    minWidth,
    search,
    style,
    width,
}) => {
    const { token } = useAntDesignTheme();

    return (
        <div
            style={{
                background: token.colorBgElevated,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                boxSizing: 'border-box',
                boxShadow: token.boxShadowSecondary,
                display: 'flex',
                flexDirection: 'column',
                maxHeight,
                minWidth,
                overflow: 'hidden',
                width,
                ...style,
            }}
        >
            {search ? (
                <div
                    style={{
                        background: token.colorBgElevated,
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        padding: '0.25rem',
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    <SearchFilterInput
                        ariaLabel={search.ariaLabel}
                        bordered={search.bordered ?? false}
                        disabled={search.disabled}
                        focusOnMount={search.autoFocus ?? true}
                        iconColor={search.iconColor}
                        onKeyDown={search.onKeyDown}
                        placeholder={search.placeholder}
                        setValue={search.setValue}
                        size={search.size ?? 'middle'}
                        suffix={search.suffix}
                        value={search.value}
                    />
                </div>
            ) : null}
            {header ? (
                <div
                    style={{
                        background: token.colorBgElevated,
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    {header}
                </div>
            ) : null}
            <OverflowFadeScrollArea
                {...contentProps}
                containerStyle={{ flex: '1 1 auto', minHeight: 0 }}
                contentStyle={contentInnerStyle}
                data-dropdown-scroll-region='true'
                disabled={contentScrollDisabled}
                surface='elevated'
                style={{
                    minHeight: 0,
                    scrollbarColor: `${token.colorBorder} transparent`,
                    scrollbarWidth: 'thin',
                    ...contentStyle,
                }}
            >
                {children}
            </OverflowFadeScrollArea>
            {actions ? <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}` }}>{actions}</div> : null}
            {footer}
        </div>
    );
};

interface DropdownPanelMenuProps extends MenuProps {
    children?: ReactNode;
}

const DropdownPanelMenu: FC<DropdownPanelMenuProps> = ({ children, style, ...props }) => (
    <Menu
        {...props}
        style={{
            background: 'transparent',
            border: 0,
            borderRadius: 0,
            boxShadow: 'none',
            paddingBlock: '0.25rem',
            ...style,
        }}
    >
        {children}
    </Menu>
);

interface DropdownMenuPanelProps extends Omit<DropdownPanelMenuProps, 'style'> {
    panelStyle?: CSSProperties;
    style?: CSSProperties;
}

const withoutUndefinedValues = (style: CSSProperties): CSSProperties =>
    Object.fromEntries(Object.entries(style).filter(([, value]) => value !== undefined)) as CSSProperties;

const withoutLegacyPanelChrome = (style: CSSProperties): CSSProperties => {
    const menuStyle = { ...style };

    delete menuStyle.border;
    delete menuStyle.borderRadius;
    delete menuStyle.boxShadow;

    return menuStyle;
};

const DropdownMenuPanel: FC<DropdownMenuPanelProps> = ({ children, panelStyle, style, ...props }) => {
    // The shared panel owns border, radius, and shadow tokens; legacy Menu
    // overrides are intentionally discarded instead of leaking into its chrome.
    const {
        bottom,
        height,
        left,
        margin,
        marginBlock,
        marginBlockEnd,
        marginBlockStart,
        marginBottom,
        marginInline,
        marginInlineEnd,
        marginInlineStart,
        marginLeft,
        marginRight,
        marginTop,
        maxHeight,
        maxWidth,
        minHeight,
        minWidth,
        overflow,
        overflowX,
        overflowY,
        position,
        right,
        top,
        width,
        zIndex,
        ...menuStyle
    } = withoutLegacyPanelChrome(style ?? {});

    const contentStyle = withoutUndefinedValues({ overflow, overflowX, overflowY });
    const legacyPanelStyle = withoutUndefinedValues({
        bottom,
        height,
        left,
        margin,
        marginBlock,
        marginBlockEnd,
        marginBlockStart,
        marginBottom,
        marginInline,
        marginInlineEnd,
        marginInlineStart,
        marginLeft,
        marginRight,
        marginTop,
        maxWidth,
        minHeight,
        position,
        right,
        top,
        zIndex,
    });

    return (
        <DropdownPanel
            contentStyle={contentStyle}
            maxHeight={maxHeight}
            minWidth={minWidth}
            width={width}
            style={{
                ...legacyPanelStyle,
                ...panelStyle,
            }}
        >
            <DropdownPanelMenu {...props} style={menuStyle}>
                {children}
            </DropdownPanelMenu>
        </DropdownPanel>
    );
};

interface DropdownMenuLabelProps {
    children: ReactNode;
    extra?: ReactNode;
    icon?: ReactNode;
    style?: CSSProperties;
}

const DropdownMenuLabel: FC<DropdownMenuLabelProps> = ({ children, extra, icon, style }) => {
    const { token } = useAntDesignTheme();

    return (
        <div
            style={{
                alignItems: 'center',
                color: token.colorTextTertiary,
                display: 'flex',
                gap: '0.25rem',
                minHeight: '1rem',
                padding: '0.375rem 0.75rem 0.25rem',
                ...style,
            }}
        >
            {icon}
            <small>{children}</small>
            {extra ? <span style={{ display: 'flex', marginLeft: 'auto' }}>{extra}</span> : null}
        </div>
    );
};

interface DropdownNestedHeaderProps {
    avatar?: DropdownOptionAvatarProps;
    backLabel?: string;
    description?: ReactNode;
    onBack: () => void;
    title: ReactNode;
}

/** Shared back-navigation header for nested dropdown picker views. */
const DropdownNestedHeader: FC<DropdownNestedHeaderProps> = ({
    avatar,
    backLabel = 'Back to Options',
    description,
    onBack,
    title,
}) => (
    <div style={{ alignItems: 'center', display: 'flex', gap: '0.25rem', padding: '0.375rem 0.75rem' }}>
        <Button
            aria-label={backLabel}
            icon={<TbArrowLeft size='1rem' />}
            onClick={(event) => {
                event.stopPropagation();
                onBack();
            }}
            size='small'
            type='text'
        />
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <DropdownOptionContent avatar={avatar} description={description} singleLine title={title} />
        </div>
    </div>
);

export {
    DropdownMenuLabel,
    DropdownMenuPanel,
    DropdownNestedHeader,
    DropdownPanelMenu,
    FLOATING_DROPDOWN_OVERLAY_STYLE,
};
export type { DropdownMenuPanelProps, DropdownPanelProps, DropdownPanelSearchProps };
export default DropdownPanel;
